import React, { ReactNode, useCallback, useMemo } from 'react';
import {
  Box,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  TablePagination,
  Tooltip,
  Checkbox,
  Typography,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import { ChipsCell } from './components/ChipsCell';
import { StatusPill } from './components/StatusPill';
import { ThumbnailGroup } from './components/ThumbnailGroup';
import type { BasicRow, MediaItem, ShowImagePreview } from '@/entities/table';

// StatusKey type now lives with getStatusColor; not needed here

type ActionLabel = 'view' | 'edit' | 'delete' | 'play' | 'stop';

// subcomponents and utils now imported from sibling files

type Column = {
  id: string;
  label?: ReactNode;
  sx?: Record<string, unknown>;
  type?: 'chips' | 'image' | string;
};

type ActionSpec = {
  label: ActionLabel;
  disabled?: boolean;
  tooltip?: string;
};

// shared in ./types

type CustomTableProps = {
  columns: Column[];
  rows: BasicRow[];
  page: number;
  setPage?: (page: number) => void;
  rowsPerPage: number;
  rowsPerPageOptions?: readonly number[];
  setRowsPerPage?: (n: number) => void;
  totalItems: number;
  editAction?: (row: BasicRow) => void;
  isEditable?: (state?: string) => boolean;
  enableRowSelection?: boolean;
  selectedIds?: BasicRow[];
  setSelectedIds?: (rows: BasicRow[]) => void;
  disableAction?: boolean;
  viewAction?: (row: BasicRow) => void;
  disableRowPerPage?: boolean;
  paginationPosition?: 'fixed' | 'inline' | 'none';
  actions?: ActionSpec[];
  getActionsForRow?: (row: BasicRow) => ActionSpec[];
  showImagePreview?: ShowImagePreview;
  deleteAction?: (row: BasicRow) => void;
  playAction?: (row: BasicRow) => void;
  stopAction?: (row: BasicRow) => void;
  fixedLayout?: boolean;
  hideStatusBlinker?: boolean;
  beforeRows?: ReactNode;
  statusColorMap?: Record<string, string>;
  // Drag & drop support
  onRowDragOver?: (
    e: React.DragEvent<HTMLTableRowElement>,
    row: BasicRow
  ) => void;
  onRowDrop?: (e: React.DragEvent<HTMLTableRowElement>, row: BasicRow) => void;
  onRowDragLeave?: (e: React.DragEvent<HTMLTableRowElement>) => void;
  dragOverRowId?: string | null;
  dragOverValid?: boolean | null;
};

const actionIconMap: Record<ActionLabel, ReactNode> = {
  view: <VisibilityIcon />,
  edit: <EditIcon />,
  delete: <DeleteIcon />,
  play: <PlayArrowIcon />,
  stop: <StopIcon />,
};

export const CustomTable: React.FC<CustomTableProps> = ({
  columns,
  rows,
  page,
  setPage = () => {},
  rowsPerPage,
  rowsPerPageOptions = [10, 25, 50, 100],
  setRowsPerPage = () => {},
  totalItems,
  editAction = () => {},
  isEditable = () => true,
  enableRowSelection = false,
  selectedIds = [],
  setSelectedIds = () => {},
  disableAction = false,
  viewAction = () => {},
  disableRowPerPage = false,
  paginationPosition = 'fixed',
  actions = [{ label: 'view', disabled: false }, { label: 'edit' }],
  getActionsForRow,
  showImagePreview = () => {},
  deleteAction = () => {},
  playAction = () => {},
  stopAction = () => {},
  fixedLayout = false,
  hideStatusBlinker = false,
  statusColorMap,
  beforeRows,
  onRowDragOver,
  onRowDrop,
  onRowDragLeave,
  dragOverRowId,
  dragOverValid,
}) => {
  const handleActionClick = useCallback(
    (type: ActionLabel, row: BasicRow) => {
      switch (type) {
        case 'edit':
          return editAction(row);
        case 'view':
          return viewAction(row);
        case 'delete':
          return deleteAction(row);
        case 'play':
          return playAction(row);
        case 'stop':
          return stopAction(row);
      }
    },
    [deleteAction, editAction, viewAction, playAction, stopAction]
  );

  const getNestedValue = (obj: Record<string, any>, path?: string): any => {
    return path
      ?.split('.')
      ?.reduce((acc: any, part: string) => (acc ? acc?.[part] : acc), obj);
  };

  const handleRowSelection = useCallback(
    (row: BasicRow, checked: boolean) => {
      if (!setSelectedIds) return;

      if (checked) {
        setSelectedIds([...selectedIds, row]);
      } else {
        setSelectedIds(
          selectedIds.filter(selected => (selected as BasicRow)?.id !== row?.id)
        );
      }
    },
    [setSelectedIds, selectedIds]
  );

  // moved ChipsCell, StatusPill, ThumbnailGroup and util into separate files

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        ...(paginationPosition === 'inline'
          ? { flex: 1, minHeight: 0, overflow: 'hidden' }
          : {}),
      }}
    >
      <Box
        sx={
          paginationPosition === 'inline'
            ? { flex: 1, minHeight: 0, overflow: 'auto' }
            : {}
        }
      >
        <Table
          sx={{
            marginBottom: '1rem',
            tableLayout: fixedLayout ? 'fixed' : 'auto',
            width: '100%',
          }}
        >
          <TableHead>
            <TableRow
              sx={{
                '& td, & th': {
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  backgroundColor: 'background.paper',
                },
              }}
            >
              {enableRowSelection && (
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={useMemo(
                      () =>
                        rows?.length > 0 &&
                        selectedIds?.length === rows?.length,
                      [rows, selectedIds]
                    )}
                    indeterminate={useMemo(
                      () =>
                        Boolean(
                          selectedIds?.length > 0 &&
                            selectedIds?.length < rows?.length
                        ),
                      [rows, selectedIds]
                    )}
                    onChange={useCallback(
                      (e: React.ChangeEvent<HTMLInputElement>) => {
                        if (e.target.checked) {
                          setSelectedIds(rows);
                        } else {
                          setSelectedIds([]);
                        }
                      },
                      [rows, setSelectedIds]
                    )}
                  />
                </TableCell>
              )}

              {columns
                ?.filter(col => (disableAction ? col?.id !== 'actions' : true))
                ?.map((col, index) => (
                  <TableCell
                    key={index}
                    sx={{
                      color: 'text.primary',
                      fontWeight: 600,
                      ...(fixedLayout && col?.sx),
                    }}
                  >
                    {col?.label || ''}
                  </TableCell>
                ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {beforeRows}
            {rows?.map(row => {
              const isDragOver = dragOverRowId === String(row.id);
              const dragBorderColor = isDragOver
                ? dragOverValid
                  ? 'success.main'
                  : 'error.main'
                : undefined;
              const dragBgColor = isDragOver
                ? dragOverValid
                  ? 'rgba(76, 175, 80, 0.08)'
                  : 'rgba(244, 67, 54, 0.08)'
                : undefined;

              return (
                <TableRow
                  key={row.id}
                  onDragOver={
                    onRowDragOver
                      ? (e: React.DragEvent<HTMLTableRowElement>) =>
                          onRowDragOver(e, row)
                      : undefined
                  }
                  onDrop={
                    onRowDrop
                      ? (e: React.DragEvent<HTMLTableRowElement>) =>
                          onRowDrop(e, row)
                      : undefined
                  }
                  onDragLeave={onRowDragLeave}
                  sx={{
                    '& td, & th': {
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      color: 'text.primary',
                    },
                    '&:hover': {
                      backgroundColor: row.disabled
                        ? 'transparent'
                        : 'action.hover',
                    },
                    opacity: row.disabled ? 0.4 : 1,
                    pointerEvents: row.disabled ? 'none' : 'auto',
                    // Drag-over visual feedback
                    ...(isDragOver && {
                      outline: '2px solid',
                      outlineColor: dragBorderColor,
                      backgroundColor: dragBgColor,
                      cursor: dragOverValid ? 'copy' : 'no-drop',
                    }),
                    transition:
                      'background-color 0.2s ease-in-out, outline 0.2s ease-in-out',
                  }}
                >
                  {enableRowSelection && (
                    <TableCell padding="checkbox">
                      <Checkbox
                        disabled={row.disabled}
                        checked={Boolean(
                          selectedIds.find(
                            selected => (selected as BasicRow)?.id === row?.id
                          )
                        )}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          handleRowSelection(row, e.target.checked);
                        }}
                      />
                    </TableCell>
                  )}

                  {columns?.map(col => {
                    const cellKey = col?.id;
                    const style = col?.sx as any;
                    const value = getNestedValue(
                      row as unknown as Record<string, any>,
                      cellKey
                    );

                    if (cellKey === 'state') {
                      return (
                        <TableCell key={cellKey} sx={fixedLayout ? style : {}}>
                          <StatusPill
                            value={String(value)}
                            active={(row as any)?.status === 'Active'}
                            hideBlinker={hideStatusBlinker}
                            colorMap={statusColorMap}
                          />
                        </TableCell>
                      );
                    }

                    if (col?.type === 'chips' || cellKey === 'screens') {
                      return (
                        <TableCell
                          key={cellKey}
                          sx={{
                            ...(fixedLayout ? style : {}),
                            fontSize: '14px',
                            fontStyle: 'normal',
                            fontWeight: 400,
                            lineHeight: '143%',
                            letterSpacing: '0.17px',
                          }}
                        >
                          <ChipsCell chips={value} />
                        </TableCell>
                      );
                    }

                    if (cellKey === 'actions') {
                      const rowActions = getActionsForRow
                        ? getActionsForRow(row)
                        : actions;

                      return (
                        <TableCell key={cellKey} sx={fixedLayout ? style : {}}>
                          {(value as ReactNode) ||
                            rowActions.map(actionType => {
                              const editable =
                                actionType.label === 'edit'
                                  ? isEditable(
                                      String((row as any)?.state)?.toUpperCase()
                                    )
                                  : true;
                              const disabled =
                                typeof actionType.disabled !== 'boolean'
                                  ? !editable
                                  : actionType.disabled;
                              const tooltipTitle =
                                actionType.tooltip ||
                                actionType.label.charAt(0).toUpperCase() +
                                  actionType.label.slice(1);

                              return (
                                <Tooltip
                                  key={actionType.label}
                                  title={tooltipTitle}
                                  arrow
                                  enterDelay={300}
                                  leaveDelay={100}
                                  PopperProps={{
                                    style: { zIndex: 1300 },
                                  }}
                                >
                                  <Box
                                    component="span"
                                    sx={{
                                      display: 'inline-flex',
                                      cursor: disabled
                                        ? 'not-allowed'
                                        : 'pointer',
                                      pointerEvents: 'auto',
                                    }}
                                  >
                                    <IconButton
                                      size="small"
                                      sx={{
                                        color: 'primary.main',
                                        ml: 1,
                                        '&:hover': {
                                          backgroundColor: 'action.hover',
                                        },
                                      }}
                                      disabled={disabled}
                                      onClick={() => {
                                        if (!disabled) {
                                          handleActionClick(
                                            actionType.label,
                                            row
                                          );
                                        }
                                      }}
                                    >
                                      {actionIconMap[actionType.label]}
                                    </IconButton>
                                  </Box>
                                </Tooltip>
                              );
                            })}
                        </TableCell>
                      );
                    }

                    if (col?.type === 'image') {
                      return (
                        <TableCell key={cellKey}>
                          <ThumbnailGroup
                            srcArr={value as MediaItem[] | MediaItem}
                            row={row as BasicRow}
                            onPreview={showImagePreview as ShowImagePreview}
                          />
                        </TableCell>
                      );
                    }

                    if (col?.type === 'timeInfo') {
                      const rowData = row as any;
                      const valueData =
                        typeof value === 'object' && value !== null
                          ? (value as any)
                          : null;
                      const getUserName = (user: any): string =>
                        !user
                          ? ''
                          : typeof user === 'string'
                            ? user
                            : user?.name || user?.email || '';
                      const getField = (key: string) =>
                        valueData?.[key] || rowData?.[key];
                      const getUser = (key: string) =>
                        getField(`${key}User`) || getField(key);
                      const createdBy = getUserName(getUser('createdBy'));
                      const updatedBy = getUserName(getUser('updatedBy'));
                      const displayName = updatedBy || createdBy || '';
                      const displayDate =
                        getField('updatedAt') || getField('createdAt') || '';

                      return (
                        <TableCell key={cellKey} sx={fixedLayout ? style : {}}>
                          <Tooltip
                            title={
                              <Box
                                sx={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  gap: 0.5,
                                  fontSize: '0.875rem',
                                }}
                              >
                                {getField('updatedAt') && (
                                  <Box>
                                    Last updated : {getField('updatedAt')}
                                    {updatedBy && ` by ${updatedBy}`}
                                  </Box>
                                )}
                                {getField('submittedAt') && (
                                  <Box>
                                    Submitted : {getField('submittedAt')}
                                  </Box>
                                )}
                                {getField('createdAt') && (
                                  <Box>
                                    Created : {getField('createdAt')}
                                    {createdBy && ` by ${createdBy}`}
                                  </Box>
                                )}
                              </Box>
                            }
                            arrow
                            placement="top"
                            enterDelay={300}
                            leaveDelay={100}
                            PopperProps={{ style: { zIndex: 1300 } }}
                          >
                            <Box
                              sx={{
                                cursor: 'help',
                                color: 'text.primary',
                                display: 'inline-block',
                                width: '100%',
                              }}
                            >
                              <Typography variant="body2">
                                {displayName}
                              </Typography>
                              <Typography variant="caption">
                                {displayDate}
                              </Typography>
                            </Box>
                          </Tooltip>
                        </TableCell>
                      );
                    }

                    // Handle React elements (like SlotCell components)
                    if (React.isValidElement(value)) {
                      return (
                        <TableCell
                          key={cellKey}
                          sx={{
                            ...(fixedLayout ? style : {}),
                            verticalAlign: 'middle',
                          }}
                        >
                          {value}
                        </TableCell>
                      );
                    }

                    return (
                      <TableCell key={cellKey} sx={fixedLayout ? style : {}}>
                        {typeof value === 'undefined' || value === null
                          ? ''
                          : String(value)}
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>
      {paginationPosition !== 'none' && (
        <Box
          sx={{
            width: '100%',
            flexShrink: 0,
            ...(paginationPosition === 'fixed'
              ? {
                  position: 'fixed',
                  bottom: 0,
                  left: 0,
                  right: 60,
                  backgroundColor: 'background.paper',
                  borderTop: '1px solid',
                  borderColor: 'divider',
                }
              : {}),
          }}
        >
          <TablePagination
            component="div"
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            count={totalItems}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={e => {
              const next = parseInt(e.target.value, 10);
              setRowsPerPage(next);
              setPage(0);
            }}
            rowsPerPageOptions={disableRowPerPage ? [] : rowsPerPageOptions}
            labelDisplayedRows={({ from, to, count }) =>
              count === 0 ? '0–0 of 0' : `${from}–${to} of ${count}`
            }
            sx={{
              ...(paginationPosition === 'fixed' ? { mr: '3rem' } : {}),
              '.MuiTablePagination-spacer': {
                display: 'none',
              },
              '.MuiTablePagination-toolbar': {
                paddingLeft: 0,
              },
            }}
          />
        </Box>
      )}
    </Box>
  );
};
